import { URLKeys, noddiAsync } from 'noddi-async';
import type { ErrorCodes } from 'noddi-ui-web';
import { ApiErrorMessage, NoddiButton, useAppForm, zodPhoneNumberSchema } from 'noddi-ui-web';
import { LanguageCodeType } from 'noddi-util';
import { useState } from 'react';
import { z } from 'zod';
import { createEmailSchema } from './passwordResetSchemas';
import { phoneNumberDto } from './util';

type ResetMethod = 'email' | 'sms';

type PasswordResetRequestProps = {
  onSuccess: (method: ResetMethod, options?: { sessionId?: string; expiresInSeconds?: number; email?: string }) => void;
  onCancel: () => void;
  initialEmail?: string;
  enableSmsReset?: boolean;
  translations?: {
    resetPassword: string;
    enterEmailToReset: string;
    enterPhoneToReset?: string;
    sendResetLink: string;
    cancel: string;
    resetViaEmail?: string;
    resetViaSMS?: string;
    email?: string;
    phoneNumber?: string;
    invalidEmail?: string;
  };
  language?: LanguageCodeType;
  errorCodes?: ErrorCodes;
};

const defaultTexts = {
  resetPassword: 'Reset password',
  enterEmailToReset: 'Enter your email address to receive a password reset link',
  enterPhoneToReset: 'Enter your phone number to receive a password reset link',
  sendResetLink: 'Send reset link',
  cancel: 'Cancel',
  resetViaEmail: 'Reset via email',
  resetViaSMS: 'Reset via SMS',
  email: 'Email',
  phoneNumber: 'Phone number',
  invalidEmail: 'Invalid email format'
};

export const PasswordResetRequest = ({
  onSuccess,
  onCancel,
  initialEmail,
  enableSmsReset = false,
  translations,
  language,
  errorCodes
}: PasswordResetRequestProps) => {
  const [resetMethod, setResetMethod] = useState<ResetMethod>('email');
  const texts = { ...defaultTexts, ...translations };

  return (
    <div className='flex flex-col gap-4'>
      <div className='flex flex-col gap-2'>
        <h2 className='text-lg font-semibold'>{texts.resetPassword}</h2>
        {enableSmsReset && (
          <div className='flex gap-2'>
            <NoddiButton
              type='button'
              variant={resetMethod === 'email' ? 'primary' : 'outline'}
              size='small'
              onClick={() => setResetMethod('email')}
            >
              {texts.resetViaEmail}
            </NoddiButton>
            <NoddiButton
              type='button'
              variant={resetMethod === 'sms' ? 'primary' : 'outline'}
              size='small'
              onClick={() => setResetMethod('sms')}
            >
              {texts.resetViaSMS}
            </NoddiButton>
          </div>
        )}
      </div>

      {resetMethod === 'email' ? (
        <EmailResetRequestForm
          key='email'
          initialEmail={initialEmail}
          texts={texts}
          onCancel={onCancel}
          onSuccess={onSuccess}
          errorCodes={errorCodes}
        />
      ) : (
        <PhoneResetRequestForm
          key='sms'
          texts={texts}
          language={language}
          onCancel={onCancel}
          onSuccess={onSuccess}
          errorCodes={errorCodes}
        />
      )}
    </div>
  );
};

type RequestFormTexts = typeof defaultTexts;

type RequestFormProps = {
  texts: RequestFormTexts;
  onCancel: () => void;
  onSuccess: PasswordResetRequestProps['onSuccess'];
  errorCodes?: ErrorCodes;
  language?: LanguageCodeType;
};

// `language` only reaches the phone form's PhoneInput, so the email form takes the
// props type without it rather than declaring a prop it never reads.
const EmailResetRequestForm = ({
  texts,
  onCancel,
  onSuccess,
  errorCodes,
  initialEmail
}: Omit<RequestFormProps, 'language'> & { initialEmail?: string }) => {
  const { mutateAsync: requestReset, error } = noddiAsync.usePost({
    type: URLKeys.postPasswordReset
  });

  const form = useAppForm({
    defaultValues: { email: initialEmail ?? '' } satisfies z.input<ReturnType<typeof createEmailSchema>>,
    validators: {
      onSubmit: createEmailSchema(texts)
    },
    onSubmit: async ({ value }) => {
      const res = await requestReset({ email: value.email, phoneNumber: null, resetType: 'email' });
      onSuccess('email', { expiresInSeconds: res.data.expiresInSeconds, email: value.email });
    }
  });

  return (
    <form.AppForm>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          form.handleSubmit();
        }}
      >
        <div className='flex flex-col gap-4'>
          <p className='text-sm text-muted-foreground'>{texts.enterEmailToReset}</p>
          <form.AppField name='email'>
            {/* oxlint-disable-next-line jsx-a11y/no-autofocus -- first field of the password-reset form, which is the entire page */}
            {(field) => <field.Input type='email' label={texts.email} autoComplete='email' autoFocus />}
          </form.AppField>
          {error && errorCodes && <ApiErrorMessage errorCodes={errorCodes} error={error} />}
          <div className='flex gap-2'>
            <NoddiButton type='button' variant='outline' onClick={onCancel} className='flex-1'>
              {texts.cancel}
            </NoddiButton>
            <form.SubscribeButton label={texts.sendResetLink} className='flex-1' />
          </div>
        </div>
      </form>
    </form.AppForm>
  );
};

const phoneSchema = z.object({ phoneNumber: zodPhoneNumberSchema });

const PhoneResetRequestForm = ({ texts, language, onCancel, onSuccess, errorCodes }: RequestFormProps) => {
  const { mutateAsync: requestReset, error } = noddiAsync.usePost({
    type: URLKeys.postPasswordReset
  });

  const form = useAppForm({
    defaultValues: { phoneNumber: '' } satisfies z.input<typeof phoneSchema>,
    validators: {
      onSubmit: phoneSchema
    },
    onSubmit: async ({ value }) => {
      const formattedPhone = phoneNumberDto({ mobileNumber: value.phoneNumber });
      const res = await requestReset({ phoneNumber: formattedPhone, email: null, resetType: 'sms' });
      onSuccess('sms', {
        sessionId: res.data.sessionId ?? undefined,
        expiresInSeconds: res.data.expiresInSeconds
      });
    }
  });

  return (
    <form.AppForm>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          form.handleSubmit();
        }}
      >
        <div className='flex flex-col gap-4'>
          <p className='text-sm text-muted-foreground'>{texts.enterPhoneToReset}</p>
          <form.AppField name='phoneNumber'>
            {(field) => <field.PhoneInput language={language} label={texts.phoneNumber} disableAutoFocus={false} />}
          </form.AppField>
          {error && errorCodes && <ApiErrorMessage errorCodes={errorCodes} error={error} />}
          <div className='flex gap-2'>
            <NoddiButton type='button' variant='outline' onClick={onCancel} className='flex-1'>
              {texts.cancel}
            </NoddiButton>
            <form.SubscribeButton label={texts.sendResetLink} className='flex-1' keepsFocus />
          </div>
        </div>
      </form>
    </form.AppForm>
  );
};
